import { generateTextWithAI } from '@/modules/shared/infra/Utils';

// Import the README content as a raw string
let readmeContent: any = '';
try {
    readmeContent = require('../infra/pdf_view/DYNAMIC_INVOICE_VIEWER_README.md');
    // If webpack processes it as a module with default export
    if (typeof readmeContent === 'object' && readmeContent !== null && readmeContent.default) {
        readmeContent = readmeContent.default;
    }
} catch (e) {
    console.warn('Could not load README.md, AI prompts will not include documentation');
    readmeContent = '# Dynamic Invoice Viewer\n\nDocumentation could not be loaded.';
}

export class InvoiceTemplatePlaygroundComponent {
    private apiRepository: any;
    private companyId: string | null;

    constructor(apiRepository: any, companyId: string | null = null) {
        this.apiRepository = apiRepository;
        this.companyId = companyId;
    }

    /**
     * Generate a new invoice template using AI
     */
    async generateTemplate(prompt: string): Promise<{ template: string; options: string | null }> {
        const fullPrompt = this._buildGenerationPrompt(prompt);

        // Define output schema for structured response
        const output_schema = {
            "type": "object",
            "properties": {
                "template": {
                    "type": "string",
                    "description": "HTML template string del componente Vue de factura (listo para Vue.compile)"
                },
                "options": {
                    "type": ["string", "null"],
                    "description": "String de JavaScript con el objeto de opciones del componente (data, computed, methods) o null si no se necesitan opciones personalizadas. Debe ser evaluable con new Function('return ' + options)."
                }
            },
            "required": ["template", "options"]
        };

        try {
            const response = await generateTextWithAI(fullPrompt, output_schema, false, false);

            if (response && response['response']) {
                const result = response['response'];
                return {
                    template: result.template,
                    options: result.options
                };
            } else {
                throw new Error('No response from AI');
            }
        } catch (error) {
            console.error('Error generating template:', error);
            throw new Error('Failed to generate template: ' + error.message);
        }
    }

    /**
     * Modify an existing template using AI
     */
    async modifyTemplate(modificationPrompt: string, currentTemplate: string, currentOptions: string | null): Promise<{ template: string; options: string | null }> {
        const fullPrompt = this._buildModificationPrompt(
            modificationPrompt,
            currentTemplate,
            currentOptions
        );

        // Define output schema for structured response
        const output_schema = {
            "type": "object",
            "properties": {
                "template": {
                    "type": "string",
                    "description": "HTML template string modificado del componente Vue de factura"
                },
                "options": {
                    "type": ["string", "null"],
                    "description": "String de JavaScript con el objeto de opciones modificado o null"
                }
            },
            "required": ["template", "options"]
        };

        try {
            const response = await generateTextWithAI(fullPrompt, output_schema, false, false);

            if (response && response['response']) {
                const result = response['response'];
                return {
                    template: result.template,
                    options: result.options
                };
            } else {
                throw new Error('No response from AI');
            }
        } catch (error) {
            console.error('Error modifying template:', error);
            throw new Error('Failed to modify template: ' + error.message);
        }
    }

    /**
     * Build the full prompt for initial template generation
     */
    private _buildGenerationPrompt(userPrompt: string): string {
        return `Eres un generador de componentes Vue dinámicos para facturas. Genera un componente dinámico de Vue 2 usando Vuetify para visualizar facturas.

CONTEXTO DEL SISTEMA:
El componente se renderiza dentro de este sistema de compilación dinámica:

export function getInvoiceViewerDynamicComponent(templateString, optionsString) {
    const compiled = Vue.compile(templateString)

    // Parse dynamic options from optionsString
    let dynamicMethods = {}
    let dynamicComputed = {}
    let dynamicData = () => ({})

    if (optionsString) {
        const optionsFn = new Function('return ' + optionsString)
        const options = optionsFn()
        if (options.methods) dynamicMethods = options.methods
        if (options.computed) dynamicComputed = options.computed
        if (options.data) dynamicData = options.data
    }

    return {
        render: compiled.render,
        staticRenderFns: compiled.staticRenderFns,
        props: {
            page: { type: Object, required: true },
            num_page: { type: Number, required: true },
            total_pages: { type: Number, required: true }
        },
        data() {
            const dynamicDataObj = typeof dynamicData === 'function' ? dynamicData.call(this) : {}
            return {
                invoiceable: this.page.invoiceable,
                configuration: this.page.configuration,
                rectified_inv: this.page.rectified_inv,
                total_lines: 0,
                ...dynamicDataObj
            }
        },
        computed: {
            // Computed properties disponibles (ya incluidos por defecto):
            proformaHeader, presupuestoHeader, computed_lines, tax_type_to_show,
            im_suplido_only_invoice, pageIsTotals, isLastPage, showAdditionalInfo,
            showPaymentMessage, showFooter, showFooterSmall,
            ...dynamicComputed
        },
        methods: {
            // Métodos disponibles (ya incluidos por defecto):
            formatCurrency(value), payment_message(payment_means),
            get_total_amount(invoice_line), formatInvoiceDate(date), formatField(field),
            ...dynamicMethods
        },
        components: {
            VCard, VCardTitle, VCardText, VCardActions, VSwitch, VForm,
            VBtn, VTextField, VTextarea, VSpacer, VCol, VRow, VCheckbox,
            VContainer, VIcon, VDataTable, VChip, VDivider
        }
    }
}

DATOS DISPONIBLES:
- invoiceable: Objeto con todos los datos de la factura (invoice_number, invoice_date, supplier, customer, lines, total_amount, etc.)
  - invoiceable.qr_url: URL del código QR tributario (OBLIGATORIO mostrar si existe - es un requisito legal español)
  - invoiceable.state: Estado de la factura ('draft', 'sent', 'paid')
- configuration: Configuración de la empresa (path_url para logo, footer, footer_small)
  - configuration.path_url: URL del logo de la empresa (DEBE mostrarse si existe)
- rectified_inv: Información de factura rectificativa (si aplica)
- total_lines: Número total de líneas de la factura
- computed_lines: Array de líneas con descripciones adicionales
- tax_type_to_show: 'IVA' o 'IGIC'

MÉTODOS DISPONIBLES (ya incluidos, puedes usarlos directamente):
- formatCurrency(value): Formatea números como moneda (ej: 1.234,56 €)
- payment_message(payment_means): Devuelve descripción del medio de pago
- get_total_amount(invoice_line): Calcula el total de una línea
- formatInvoiceDate(date): Formatea fecha de YYYY-MM-DD a DD/MM/YYYY
- formatField(field): Formatea campos de tipo impositivo (ej: 0.21 → '21%')

REQUISITOS OBLIGATORIOS DE DISEÑO:
1. **QR CODE (CRUCIAL - REQUISITO LEGAL ESPAÑOL):**
   - Si invoiceable.qr_url existe, DEBE mostrarse de forma prominente
   - Tamaño mínimo recomendado: 120x120 píxeles (usar width="120" height="120")
   - Ubicación sugerida: Parte superior izquierda o derecha del documento
   - Incluir etiqueta: "QR tributario:" y texto pequeño: "Factura verificable en la sede electrónica de la AEAT"
   - Ejemplo de código:
     <div v-if="invoiceable.qr_url">
       <small>QR tributario:</small><br>
       <img :src="invoiceable.qr_url" alt="Código QR" width="120" height="120"><br>
       <small style="font-size: 10px;">Factura verificable en la sede electrónica de la AEAT</small>
     </div>

2. **LOGO DE EMPRESA:**
   - Si configuration.path_url existe, mostrar el logo
   - Tamaño sugerido: max-height: 140px, max-width: 220px
   - Ubicación: Normalmente parte superior derecha (o junto al QR si existe)

El componente debe:
1. Usar los props page, num_page y total_pages que se pasan automáticamente
2. Acceder a los datos mediante this.invoiceable, this.configuration, etc.
3. Usar componentes de Vuetify disponibles
4. El tag principal debe contener un solo elemento raíz
5. Puedes aplicar estilos inline con style="..."
6. NO necesitas emitir eventos, es un componente de visualización

IMPORTANTE sobre options:
- En "data", SOLO incluye datos auxiliares adicionales para el componente (no incluyas invoiceable, configuration, etc. que ya vienen por defecto)
- Usa "computed" para propiedades calculadas adicionales
- Usa "methods" para funciones auxiliares adicionales (no incluyas formatCurrency, etc. que ya vienen por defecto)
- El formato debe ser evaluable como objeto JavaScript literal
- Si no necesitas opciones personalizadas, devuelve null

Ejemplo de options:
{
    data: () => ({
        showDetails: false,
        selectedTab: 0
    }),
    computed: {
        totalWithMarkup() {
            return this.invoiceable.total_amount * 1.15
        }
    },
    methods: {
        toggleDetails() {
            this.showDetails = !this.showDetails
        }
    }
}

REQUISITOS DEL USUARIO:
${userPrompt}

Devuelve el template HTML y las options (o null si no necesitas opciones personalizadas).`;
    }

    /**
     * Build the full prompt for template modification
     */
    private _buildModificationPrompt(userPrompt: string, currentTemplate: string, currentOptions: string | null): string {
        return `Eres un modificador de componentes Vue dinámicos para facturas. Modifica el siguiente componente Vue 2 según las instrucciones del usuario.

TEMPLATE ACTUAL:
${currentTemplate}

OPTIONS ACTUALES:
${currentOptions || 'null'}

INSTRUCCIONES DE MODIFICACIÓN:
${userPrompt}

REGLAS IMPORTANTES PARA LA MODIFICACIÓN:

1. **MANTÉN LA FUNCIONALIDAD EXISTENTE:**
   - NO elimines elementos funcionales como tablas de líneas, datos del cliente/proveedor, totales, impuestos, etc.
   - CONSERVA todas las directivas Vue existentes (v-if, v-for, :key, etc.)
   - MANTÉN los bindings de datos ({{ }}, :src, :value, etc.)

2. **CÉNTRATE EN EL ESTILO:**
   - Modifica principalmente colores, fuentes, tamaños de texto, espaciados, bordes, sombras
   - Reorganiza elementos visuales si es necesario pero sin romper la funcionalidad
   - Usa clases de Vuetify o estilos inline para los cambios visuales

3. **RESPETA LAS DIMENSIONES:**
   - El componente se renderiza en una página A4 con paginado automático
   - NO aumentes drásticamente las dimensiones verticales (height, padding vertical excesivo, etc.)
   - Mantén el contenido compacto para que quepa en una página estándar
   - Si agregas espaciado, hazlo moderadamente para no romper el paginado

4. **OPTIONS - IMPORTANTE:**
   - El componente YA TIENE estos elementos disponibles por defecto (NO los incluyas en options):
     * **data**: invoiceable, configuration, rectified_inv, total_lines
     * **computed**: proformaHeader, presupuestoHeader, computed_lines, tax_type_to_show, im_suplido_only_invoice, pageIsTotals, isLastPage, showAdditionalInfo, showPaymentMessage, showFooter, showFooterSmall
     * **methods**: formatCurrency, payment_message, get_total_amount, formatInvoiceDate, formatField
   - SOLO incluye en options:
     * **data**: Datos auxiliares adicionales (contadores, flags, estados UI, etc.) que NO existan por defecto
     * **computed**: Propiedades calculadas adicionales que NO existan por defecto
     * **methods**: Funciones auxiliares adicionales que NO existan por defecto
   - Si no necesitas agregar nada nuevo, devuelve options como null

5. **MANTÉN LA ESTRUCTURA:**
   - Conserva el elemento raíz único (div, v-container, etc.)
   - No rompas la jerarquía de componentes Vuetify
   - Preserva las tablas, listas y estructuras de datos importantes

EJEMPLO DE MODIFICACIÓN CORRECTA:
- Usuario: "Haz el encabezado más grande y azul"
- Correcto: Modificar el v-card-title del encabezado con style="font-size: 28px; color: #1976D2;"
- Incorrecto: Agregar padding: 200px que rompería el paginado

Devuelve el template y options modificados. Si no necesitas options personalizadas, devuelve null.`;
    }

    /**
     * Save the invoice template to the backend
     */
    async saveTemplate(params: { name: string; template: string; options: string | null }): Promise<any> {
        if (!this.companyId) {
            throw new Error('Company ID is required to save templates');
        }

        return await this.apiRepository.saveInvoiceTemplate({
            company_id: this.companyId,
            name: params.name,
            template: params.template,
            options: params.options
        });
    }

    /**
     * Get sample invoice data for testing
     */
    getSampleInvoiceData(type: string = 'basic', pageNum: number = 0): any {
        const samples: any = {
            basic: this._getBasicInvoiceSample(),
            discounts: this._getInvoiceWithDiscounts(),
            taxes: this._getInvoiceWithTaxes(),
            complex: this._getComplexInvoiceSample()
        };

        const invoiceable: any = samples[type] || samples.basic;
        //NO SERIA MILLOR INSTANCIAR Invoiceable?
        // Mock invoiceable methods
        invoiceable.compute = function() {
            // Mock compute method
        };

        invoiceable.get_lines_with_additional_descriptions = function() {
            const result: any[] = [];

            if (!this.lines) return result;

            // For each line, check if it has additional_line_information
            // If yes, create two entries: the line itself and an additional_info row
            this.lines.forEach((line: any) => {
                result.push(line);

                // If this line has additional_line_information, add an extra row for it
                if (line.additional_information && line.additional_information.additional_line_information) {
                    console.log('Found additional_line_information:', line.additional_information.additional_line_information);
                    result.push({
                        is_additional_info: true,
                        additional_information: line.additional_information
                    });
                }
            });

            console.log('get_lines_with_additional_descriptions result:', result.length, 'lines');
            return result;
        };

        return {
            invoiceable: invoiceable,
            configuration: {
                path_url: require('@/assets/logo_inmatic.png'),
                footer: '¡Gracias por su confianza!',
                footer_small: 'Empresa SL - CIF: B12345678'
            },
            rectified_inv: null,
            pageNum: pageNum,
            showHeader: pageNum === 0,
            showLinesHeader: true,
            showLines: true,
            firstPrintedLine: -1,
            lastPrintedLine: (invoiceable.lines && invoiceable.lines.length) || 0
        };
    }

    private _getBasicInvoiceSample(): any {
        return {
            type: 'invoice',
            name: 'Factura',
            invoice_number: 'F2025-001',
            invoice_date: '2025-01-15',
            invoice_due_date: '2025-02-15',
            start_date: '2025-01-01',
            end_date: '2025-01-31',
            state: 'sent',
            qr_url: require('@/assets/qr_test.png'),
            rectificative: false,
            external_invoice_number: null,
            signing_time: null,
            supplier: {
                type: 'juridica',
                business_name: 'Soluciones Tecnológicas SL',
                cif: 'B12345678',
                address: 'Calle Principal 123',
                post_code: '08001',
                city: 'Barcelona',
                province: 'Barcelona',
                bank_account_number: 'ES1234567890123456789012',
                trade_name: 'Soluciones Tech',
                email: 'info@solucionestech.com',
                phone: '+34 900 000 000',
                website: 'www.solucionestech.com'
            },
            customer: {
                type: 'juridica',
                business_name: 'Corporación Cliente SA',
                cif: 'A87654321',
                address: 'Avenida Comercial 456',
                post_code: '28001',
                city: 'Madrid',
                province: 'Madrid',
                trade_name: 'Corp Cliente',
                email: 'facturacion@corpclient.com'
            },
            lines: [
                {
                    description: 'Servicios de Desarrollo Web',
                    quantity: 40,
                    unit_price_without_tax: 50.00,
                    gross_amount: 2000.00,
                    discounts: [],
                    taxes: [{ rate: 0.21, amount: 420.00 }],
                    taxes_withheld: []
                },
                {
                    description: 'Diseño UI/UX',
                    quantity: 20,
                    unit_price_without_tax: 60.00,
                    gross_amount: 1200.00,
                    discounts: [],
                    taxes: [{ rate: 0.21, amount: 252.00 }],
                    taxes_withheld: []
                }
            ],
            total_gross_amount: 3200.00,
            total_gross_amount_before_tax: 3200.00,
            total_lines_discounts: 0,
            general_discounts: [],
            total_taxes: [{ rate: 0.21, base: 3200.00, amount: 672.00 }],
            total_taxes_withheld: [],
            total_amount: 3872.00,
            total_to_pay: 3872.00,
            retention_amount: 0,
            retention_concept: null,
            reimbursable_amounts: [],
            reimbursable_expense_concept: null,
            payment_means: '04',
            payment_information: 'Transferencia bancaria a IBAN: ES1234567890123456789012',
            additional_info: 'Pago en un plazo de 30 días',
            tax_type: 'iva',
            related_invoiceables_type: null
        };
    }

    private _getInvoiceWithDiscounts(): any {
        const invoice: any = this._getBasicInvoiceSample();
        invoice.invoice_number = 'F2025-002';
        // Line 1: 40 × 50 = 2000, discount 10% = 200, net = 1800
        invoice.lines[0].discounts = [{ rate: 0.10, amount: 200 }];
        invoice.lines[0].gross_amount = 1800.00;
        invoice.lines[0].taxes = [{ rate: 0.21, amount: 378.00 }]; // 1800 × 0.21 = 378
        // Line 2 stays the same: 1200 + 252 IVA
        invoice.general_discounts = [{ reason: 'Descuento por pronto pago', amount: 100 }];
        invoice.total_lines_discounts = 200;
        invoice.total_gross_amount = 3000.00; // 1800 + 1200
        invoice.total_gross_amount_before_tax = 2900.00; // 3000 - 100 (general discount)
        invoice.total_taxes = [{ rate: 0.21, base: 2900.00, amount: 609.00 }]; // 2900 × 0.21
        invoice.total_amount = 3509.00; // 2900 + 609
        invoice.total_to_pay = 3509.00;
        return invoice;
    }

    private _getInvoiceWithTaxes(): any {
        const invoice: any = this._getBasicInvoiceSample();
        invoice.invoice_number = 'F2025-003';
        invoice.lines[0].taxes_withheld = [{ rate: 0.15, amount: 300 }];
        invoice.total_taxes_withheld = [{ rate: 0.15, base: 2000.00, amount: 300 }];
        invoice.retention_amount = 300;
        invoice.retention_concept = 'IRPF';
        invoice.total_to_pay = 3572.00;
        return invoice;
    }

    private _getComplexInvoiceSample(): any {
        const invoice: any = this._getBasicInvoiceSample();

        // Complete invoice info
        invoice.invoice_number = 'F2025-004';
        invoice.external_invoice_number = 'EXT-2025-004';
        invoice.invoice_date = '2025-01-15';
        invoice.invoice_due_date = '2025-02-15';
        invoice.start_date = '2025-01-01';
        invoice.end_date = '2025-01-31';
        invoice.rectificative = false;
        invoice.signing_time = '2025-01-15 10:30:00';
        invoice.related_invoiceables_type = 'proforma';

        // Enhanced supplier info
        invoice.supplier.bank_account_number = 'ES7921000813610123456789';
        invoice.supplier.trade_name = 'Soluciones Tech';
        invoice.supplier.email = 'facturas@solucionestech.com';
        invoice.supplier.phone = '+34 932 123 456';
        invoice.supplier.website = 'www.solucionestech.com';

        // Enhanced customer info with government fields
        invoice.customer.trade_name = 'Corp Cliente';
        invoice.customer.DIRe = 'L01234567';
        invoice.customer.accounting_office = 'A12345678';
        invoice.customer.management_body = 'M87654321';
        invoice.customer.processing_unit = 'U11223344';
        invoice.customer.proposing_body = 'P99887766';

        // Line 1: 40 × 50 = 2000, discount 3% = 60, net = 1940
        invoice.lines[0].discounts = [{ rate: 0.03, amount: 60 }];
        invoice.lines[0].gross_amount = 1940.00;
        invoice.lines[0].taxes = [{ rate: 0.21, amount: 407.40 }];

        // Line 2: stays the same 20 × 60 = 1200
        // Add recargo de equivalencia 5.2% to line 2
        invoice.lines[1].taxes = [{ rate: 0.21, amount: 252.00, recargo_rate: 0.052, recargo_amount: 62.40 }]; // 1200 × 0.052 = 62.40

        // Add line 3 with discounts and withholdings
        // 10 × 100 = 1000, discount 5% = 50, net = 950
        (invoice.lines as any[]).push({
            description: 'Servicios de Consultoría - Planificación Estratégica y Soporte de Implementación con documentación detallada y reuniones de seguimiento',
            quantity: 10,
            unit_price_without_tax: 100.00,
            gross_amount: 950.00,
            discounts: [{ rate: 0.05, amount: 50 }],
            taxes: [{ rate: 0.21, amount: 199.50 }],
            taxes_withheld: [{ rate: 0.15, amount: 142.50 }],
            additional_information: {
                sender_info: {
                    budget_number: 'PROF-2024-789',
                    budget_date: '2024-12-10'
                },
                record_reference: 'EXP-2024-999',
                record_reference_date: '2024-12-18',
                additional_line_information: 'Incluye análisis de viabilidad, plan de implementación y seguimiento trimestral durante 2025'
            }
        });

        // Total line discounts: 60 + 0 + 50 = 110
        invoice.total_lines_discounts = 110;

        // General discounts: 100 + 50 = 150
        invoice.general_discounts = [
            { reason: 'Descuento por pronto pago', amount: 100 },
            { reason: 'Descuento por volumen', amount: 50 }
        ];

        // Calculation:
        // Line 1: 1940 (after line discount)
        // Line 2: 1200 (no line discount)
        // Line 3: 950 (after line discount)
        // Subtotal: 4090
        invoice.total_gross_amount = 4090.00;

        // After general discounts: 4090 - 150 = 3940
        invoice.total_gross_amount_before_tax = 3940.00;

        // IVA 21%: 3940 × 0.21 = 827.40
        // Recargo equivalencia on line 2: 1200 × 0.052 = 62.40 (already after general discount proportion)
        // Actually: (1200 - 1200*(150/4090)) × 0.052 = (1200 - 44.01) × 0.052 = 60.11
        invoice.total_taxes = [{ rate: 0.21, base: 3940.00, amount: 827.40, recargo_rate: 0.052, recargo_amount: 60.11 }];

        // Total invoice: 3940 + 827.40 + 60.11 = 4827.51
        invoice.total_amount = 4827.51;

        // Taxes withheld (IRPF 15% on line 3 after discounts):
        // Line 3 net after general discount: 950 - 950*(150/4090) = 950 - 34.84 = 915.16
        // IRPF: 915.16 × 0.15 = 137.27
        invoice.total_taxes_withheld = [{ rate: 0.15, base: 915.16, amount: 137.27 }];
        invoice.retention_amount = 137.27;
        invoice.retention_concept = 'IRPF';

        // Reimbursable expenses
        invoice.reimbursable_amounts = [250.00];
        invoice.reimbursable_expense_concept = 'Gastos de viaje y alojamiento';

        // Total to pay: 4827.51 - 137.27 (retention) + 250 (reimbursable) = 4940.24
        invoice.total_to_pay = 4940.24;

        // Additional info (multi-line)
        invoice.additional_info = 'Esta factura incluye gastos reembolsables por visitas al cliente.\nCondiciones de pago: 30 días netos.\nLos pagos tardíos incurrirán en un interés del 5% mensual.\n\nProyecto: Transformación Digital Fase 2\nNúmero de PO: PO-2024-5678';

        // Payment info
        invoice.payment_information = 'Por favor referenciar número de factura F2025-004 en la transferencia';

        // Proforma header info for first line
        (invoice.lines[0] as any).additional_information = {
            sender_info: {
                budget_number: 'PROF-2024-123',
                budget_date: '2024-12-15'
            },
            record_reference: 'EXP-2024-456',
            record_reference_date: '2024-12-20'
        };

        return invoice;
    }
}
